'use client';

import React, { useContext } from 'react';
import { BanknotesIcon, BriefcaseIcon, MapPinIcon, TagIcon } from '@heroicons/react/16/solid';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/animations/shift-away-subtle.css';
import classNames from 'classnames';
import Link from 'next/link';
import { GateStyleContext } from './GateStyleContext';
import SafeTooltip from './SafeTooltip';
import { resolveSetting } from '../../utils/settingsHelper';
import { getLinkClasses, getLinkStyle } from '../selectors/Link/LinkHelpers';
import { getDivClasses, getDivStyle } from '../selectors/Basic/divHelpers';
import { formatDisplayFieldValue } from '../selectors/Gate/JobCountHelpers';
import Flag from '../static/Flag';
import Icon from '../selectors/Link/Icon';
import { getFontSizeClasses } from '../selectors/Text/Text';

function Location({ children, show, iconColor }) {
	if (!show) return null;
	return (
		<div className="flex gap-2 truncate">
			<MapPinIcon className="h-4 w-4 flex-shrink-0" style={{ color: iconColor }} />
			<p className="truncate">{children}</p>
		</div>
	);
}

function Salary({ children, show, iconColor }) {
	if (!show) return null;
	return (
		<div className="flex gap-2 truncate">
			<BanknotesIcon className="h-4 w-4 flex-shrink-0" style={{ color: iconColor }} />
			<p className="truncate">{children}</p>
		</div>
	);
}

function EmploymentForms({ items, show, iconColor, language }) {
	if (!items || !show || items.length === 0) return null;
	return (
		<div className="flex gap-2 truncate">
			<BriefcaseIcon className="h-4 w-4 flex-shrink-0" style={{ color: iconColor }} />
			<p className="truncate text-xs">
				{items.map((i) => (i.locale ? i.locale[language] : i.name)).filter((i) => i).join(', ')}
			</p>
		</div>
	);
}

function getPreviewFieldValue(fieldName, fieldsMetaMap, itemId) {
	const meta = fieldsMetaMap?.[fieldName];
	if (!meta) return null;
	if (meta.type === 'date') return new Date();
	if (meta.options?.length) return meta.options[itemId % meta.options.length];
	return null;
}

function CustomFieldBadges({ item, fields, fieldsMetaMap, active, iconColor, locale }) {
	if (!fields || fields.length === 0) return null;
	return fields.map((fieldName) => {
		const meta = fieldsMetaMap?.[fieldName];
		const text = active
			? formatDisplayFieldValue(item.fields?.[fieldName], meta?.type, locale)
			: formatDisplayFieldValue(getPreviewFieldValue(fieldName, fieldsMetaMap, item.id), meta?.type, locale);
		if (!text) return null;
		return (
			<SafeTooltip key={fieldName} placement="top" content={meta?.label || fieldName}>
				<div className="flex gap-2 truncate">
					<TagIcon className="h-4 w-4 flex-shrink-0" style={{ color: iconColor }} />
					<p className="truncate text-xs">{text}</p>
				</div>
			</SafeTooltip>
		);
	});
}

function JobButton({ id, button, settings, active, buttonText, iconBefore }) {
	return (
		<Link
			style={getLinkStyle(button, settings)}
			className={classNames(
				getLinkClasses(button, settings),
				'block',
				'text-center gap-x-2',
				'hover:opacity-75',
			)}
			href={active ? `/jobs/${id}` : '#'}
		>
			<div className="inline-flex items-center align-middle text-center gap-x-2">
				{iconBefore && (iconBefore.length === 2 ? <Flag code={iconBefore} /> : <Icon value={iconBefore} />)}
				{buttonText}
			</div>
		</Link>
	);
}

export function RenderItem({ item }) {
	const {
		settings, variant, active, showLocation, showEmploymentForms, showSalary, iconBefore, buttonText, titleFontSize,
		displayFields, fieldsMetaMap, pageLanguage, ...rest
	} = useContext(GateStyleContext);

	const button = {};
	Object.keys(rest).forEach((key) => {
		if (key.startsWith('button.')) {
			button[key.substring(7)] = rest[key];
		}
	});

	const iconColor = resolveSetting(button.background, settings) === 'transparent' ? resolveSetting(button.color, settings) : resolveSetting(button.background, settings);

	const fontSizeClass = getFontSizeClasses(titleFontSize || 22, settings);

	const divClasses = getDivClasses(rest, settings);
	const interactiveClasses = active && rest.interactive ? 'transition ease-in-out delay-50 hover:-translate-y-4 duration-300' : '';
	const divStyleCol = getDivStyle({ ...rest, alignItems: 'start', display: 'block' }, settings);
	const divStyleRow = getDivStyle({ ...rest, alignItems: 'stretch', display: 'block' }, settings);

	switch (variant) {
	case 'list':
		return (
			<li
				className={classNames('truncate py-5 px-6')}
			>
				<div className="flex flex-col @sm:flex-row @sm:items-center w-full justify-between gap-y-2 @sm:gap-x-6">
					<div className="min-w-0 overflow-hidden">
						<div className="flex items-center gap-x-3">
							<p className={classNames(fontSizeClass, 'text-left font-bold')}>{item.title}</p>
						</div>
						<div className="mt-1 flex flex-col @sm:flex-row @sm:items-center gap-2 text-xs leading-5">
							{item.location && (<Location show={showLocation} iconColor={iconColor}>{item.location}</Location>)}
							{item.salary && (<Salary show={showSalary} iconColor={iconColor}>{item.salary}</Salary>)}
							<EmploymentForms items={item.employmentForms} show={showEmploymentForms} iconColor={iconColor} language={item.language} />
							<CustomFieldBadges item={item} fields={displayFields} fieldsMetaMap={fieldsMetaMap} active={active} iconColor={iconColor} locale={pageLanguage} />
						</div>
					</div>
					<div className="flex flex-none text-center w-full @sm:w-auto items-center gap-x-4">
						<JobButton id={item.id} button={button} settings={settings} active={active} buttonText={buttonText} iconBefore={iconBefore} />
					</div>
				</div>
			</li>
		);
	case 'list2':
		return (
			<li
				className={classNames(
					'py-5 px-6',
					divClasses,
					interactiveClasses,
				)}
				style={divStyleRow}
			>
				<div className="flex flex-col @sm:flex-row @sm:items-center w-full justify-between gap-y-2 @sm:gap-x-6 w-full">
					<div className="min-w-0 overflow-hidden">
						<div className="flex items-center gap-x-3">
							<p className={classNames(fontSizeClass, 'text-left font-bold')}>{item.title}</p>
						</div>
						<div className="mt-1 flex flex-col @sm:flex-row  @sm:items-center gap-x-2 text-xs leading-5">
							{item.location && (<Location show={showLocation} iconColor={iconColor}>{item.location}</Location>)}
							{item.salary && (<Salary show={showSalary} iconColor={iconColor}>{item.salary}</Salary>)}
							<EmploymentForms items={item.employmentForms} show={showEmploymentForms} iconColor={iconColor} language={item.language} />
							<CustomFieldBadges item={item} fields={displayFields} fieldsMetaMap={fieldsMetaMap} active={active} iconColor={iconColor} locale={pageLanguage} />
						</div>
					</div>
					<div className="flex flex-none text-center w-full @sm:w-auto items-center gap-x-4">
						<JobButton id={item.id} button={button} settings={settings} active={active} buttonText={buttonText} iconBefore={iconBefore} />
					</div>
				</div>
			</li>
		);

	case 'card':
		return (
			<li
				// href={active ? `/jobs/${item.id}` : '#'}
				style={divStyleCol}
				className={classNames('relative p-6 block', divClasses, interactiveClasses)}
			>
				<div className="overflow-hidden w-full h-full flex flex-col justify-between">
					<div className="overflow-hidden w-full h-full flex flex-col gap-2">
						<div className="mb-2">
							<h2 className={classNames(fontSizeClass, 'text-xl font-bold text-left')}>{item.title}</h2>
						</div>
						<div className="my-1 flex items-center overflow-hidden gap-x-2 text-xs leading-5">
							{item.location && (<Location show={showLocation} iconColor={iconColor}>{item.location}</Location>)}
							{item.salary && (<Salary show={showSalary} iconColor={iconColor}>{item.salary}</Salary>)}
						</div>
						<EmploymentForms items={item.employmentForms} show={showEmploymentForms} iconColor={iconColor} language={item.language} />
						<CustomFieldBadges item={item} fields={displayFields} fieldsMetaMap={fieldsMetaMap} active={active} iconColor={iconColor} locale={pageLanguage} />
					</div>
					<div className="mt-3">
						<JobButton id={item.id} button={button} settings={settings} active={active} buttonText={buttonText} iconBefore={iconBefore} />
					</div>
				</div>
			</li>
		);
	case 'card2':
	default:
		return (
			<li
				key={item.id}
				style={divStyleCol}
				className={classNames(divClasses, interactiveClasses)}
			>
				<div className="flex w-full h-full items-center justify-between overflow-hidden p-6">
					<div className="flex-1 flex flex-col h-full w-full justify-between">
						<div className="space-y-2">
							<div className="flex items-center space-x-3">
								<h3 className={classNames(fontSizeClass, 'font-bold')}>{item.title}</h3>
							</div>
							<div className="mt-1 flex items-center gap-x-2 text-xs leading-5">
								{item.location && (<Location show={showLocation} iconColor={iconColor}>{item.location}</Location>)}
								{item.salary && (<Salary show={showSalary} iconColor={iconColor}>{item.salary}</Salary>)}
								<EmploymentForms items={item.employmentForms} show={showEmploymentForms} iconColor={iconColor} language={item.language} />
								<CustomFieldBadges item={item} fields={displayFields} fieldsMetaMap={fieldsMetaMap} active={active} iconColor={iconColor} locale={pageLanguage} />
							</div>
						</div>
						<div className="mt-3">
							<JobButton id={item.id} button={button} settings={settings} active={active} buttonText={buttonText} iconBefore={iconBefore} />
						</div>
					</div>
				</div>
			</li>
		);
	}
}
